import React, { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Link, Typography, Tooltip, Skeleton, Chip } from "@mui/material";
import { Icon } from "@iconify/react";
import { getDueDateStyles } from "../../../Utils/critiqueDueDateUtils";
import { useIsTextOverflowing } from "../../../hooks/useIsTextOverflowing";
import dataService from "../../../services/data.service";
import CritiqueStatusChip from "../../../pages/critique/components/CritiqueStatusChip";
import PlanTypeChip from "../../../pages/critique/components/PlanTypeChip";

/**
 * Neutral icon + text pill for critique context (hotel, period).
 * Colours match the "Read only" chip; long values ellipsize and show the full text in a tooltip.
 */
const InfoPill = ({ icon, value, maxWidth = 240, color = "#637381", bgcolor = "#919EAB29" }) => {
  const textRef = useRef(null);
  const isOverflowing = useIsTextOverflowing(textRef, [value]);
  if (!value) return null;

  return (
    <Tooltip title={isOverflowing ? value : ""} arrow placement="top">
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          height: 24,
          maxWidth,
          px: "6px",
          borderRadius: "6px",
          color,
          bgcolor,
          flexShrink: 1,
          minWidth: 0,
        }}
      >
        <Icon icon={icon} width={16} height={16} style={{ flexShrink: 0 }} />
        <Typography
          ref={textRef}
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: "12px",
            lineHeight: "20px",
            color: "inherit",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const MAX_TITLE_LENGTH = 34;

/**
 * Critique name, cut to MAX_TITLE_LENGTH characters plus "..." so the header stays on a single line;
 * full name in a tooltip when cut.
 */
const CritiqueTitle = ({ name }) => {
  const isTruncated = name.length > MAX_TITLE_LENGTH;
  const displayName = isTruncated ? `${name.substring(0, MAX_TITLE_LENGTH)}...` : name;

  return (
    <Tooltip
      title={isTruncated ? name : ""}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 300,
            whiteSpace: "normal",
            wordWrap: "break-word",
          },
        },
      }}
    >
      <Typography
        component="h1"
        sx={{
          fontSize: "24px",
          lineHeight: "36px",
          fontWeight: 700,
          color: "#212B36",
          whiteSpace: "nowrap",
        }}
      >
        {displayName}
      </Typography>
    </Tooltip>
  );
};

/**
 * Header content for the Critique Details (Edit Critique) page.
 * Shows: "Critique templates" link (→ all critiques), critique name, hotel, period being critiqued,
 * and due date or status chip.
 * Due date styling matches AllCritiquesCard (overdue, reaching soon, enough time).
 * Status chip (Completed/OVERDUE_SUBMITTED) matches AllCritiquesCard.
 * Right-side actions (watchers, export, Save, Submit) are rendered by Header.js.
 */
const CritiqueDetailsHeader = ({ critiqueData, loading = false, showReadOnlyLabel = false }) => {
  const navigate = useNavigate();
  const critiqueName = critiqueData?.critique_name || critiqueData?.title || "Untitled Critique";
  const dueDateLabel = critiqueData?.due_date_label || critiqueData?.dueDateLabel || "";
  const dueDate = critiqueData?.due_date || critiqueData?.dueDate || null;
  const status = critiqueData?.status || "due";
  const isCompleted = critiqueData?.is_completed === true;
  const planType = critiqueData?.plan_type || critiqueData?.planType || null;
  const periodLabel = critiqueData?.period_label || "";

  /** Match AllCritiques / PDF cover: the API sends "Property {id}" as a placeholder, so resolve the real name. */
  const propertyName = useMemo(() => {
    const pid = critiqueData?.property_id;
    let name = critiqueData?.property_name;
    if (!name || (typeof name === "string" && name.startsWith("Property "))) {
      const prop = pid != null ? dataService.getPropertyById(pid) : null;
      name = prop ? prop.name : name;
    }
    return name || "";
  }, [critiqueData?.property_id, critiqueData?.property_name]);

  const handleBackToAllCritiques = () => {
    navigate("/critique/all");
  };

  const dueDateStyles = getDueDateStyles(dueDate);

  const renderStatusOrDueDate = () => {
    if (isCompleted) {
      return <CritiqueStatusChip status={status} />;
    }
    if (dueDateLabel) {
      return (
        <InfoPill
          icon="solar:alarm-bold-duotone"
          value={dueDateLabel}
          color={dueDateStyles.color}
          bgcolor={dueDateStyles.backgroundColor}
        />
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" gap="2px" flexDirection="column">
        <Box sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}>
          <Skeleton variant="text" width={120} height={22} sx={{ borderRadius: "4px" }} />
        </Box>
        <Box display="flex" flexDirection="row" alignItems="center" gap={1} sx={{ minHeight: 36 }}>
          <Skeleton variant="rounded" width={280} height={36} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: "6px" }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" gap="2px" flexDirection="column" sx={{ minWidth: 0 }}>
      {/* Breadcrumb: Critique templates */}
      <Box sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}>
        <Link
          underline="hover"
          color="inherit"
          component="button"
          onClick={handleBackToAllCritiques}
          className="!text-sm !leading-[22px] !text-[#637381]"
          sx={{ cursor: "pointer", background: "none", border: "none", padding: 0, font: "inherit" }}
        >
          All critiques
        </Link>
      </Box>

      {/* Critique name, hotel, period, and due date / status chip row */}
      <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexWrap="nowrap" sx={{ minHeight: 24, minWidth: 0 }}>
        <CritiqueTitle name={critiqueName} />
        <InfoPill icon="solar:buildings-2-bold-duotone" value={propertyName} />
        {/* Same blue as the period chip on AllCritiquesCard */}
        <InfoPill
          icon="solar:calendar-bold-duotone"
          value={periodLabel}
          color="#0F3C7E"
          bgcolor="rgba(42, 91, 162, 0.16)"
        />
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flexShrink: 1, whiteSpace: "nowrap" }}>
          {renderStatusOrDueDate()}
          <PlanTypeChip planType={planType} />
          {showReadOnlyLabel && (
            <Chip
              size="small"
              label="Read only"
              sx={{
                backgroundColor: "#919EAB29",
                color: "#637381",
                borderRadius: "6px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CritiqueDetailsHeader;
